import React, { useEffect, useMemo, useRef, useState } from "react";
import { ProvisionalContentMedium } from "../types";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { useIsMobile } from "../hook/useIsMobile";
import { MdDragIndicator } from "react-icons/md";
import { eventEmitter } from "../utils/eventEmitter";
import { Cloudinary } from "@cloudinary/url-gen";
import { thumbnail } from "@cloudinary/url-gen/actions/resize";
import { AdvancedImage } from "@cloudinary/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

type MediaProps = {
  allowEdit: boolean;
  cm: ProvisionalContentMedium;
  index: number;
  deleteContentMedia: (
    contentMediaId: ProvisionalContentMedium["provisional_id"]
  ) => void;
  changePosition: (cm: ProvisionalContentMedium, index: number) => void;
  hasNoPointer?: boolean;
};

const cld = new Cloudinary({
  cloud: {
    cloudName: "dcmekntwa",
  },
});

const DraggableMedia = ({
  allowEdit,
  cm,
  index,
  deleteContentMedia,
  changePosition,
  hasNoPointer,
}: MediaProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [_, dragRef] = useDrag(
    () => ({
      type: "drag_item",
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      item: (monitor: any) => {
        return { sourceContentMedia: cm };
      },
    }),
    []
  );
  type DragItem = { sourceContentMedia: ProvisionalContentMedium };
  const [{ isDragging }, drop] = useDrop<
    DragItem,
    void,
    { isDragging: boolean }
  >({
    accept: "drag_item",
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      changePosition(item.sourceContentMedia, index + 1);
    },
    drop(item: DragItem, monitor: any) {
      changePosition(item.sourceContentMedia, index + 1);
    },
  });
  dragRef(drop(ref));

  const [bluredImage, setBluredImage] = useState(false);

  useEffect(() => {
    const blurImage = (e: { originalUrl: string }) => {
      // On ne blur que le content medium qui a changé
      const cmHasChanged = e.originalUrl === cm.url;
      setBluredImage(cmHasChanged);
    };
    eventEmitter.on("editRendering", blurImage);
    return () => eventEmitter.off("editRendering", blurImage);
  }, [cm]);

  return (
    <div
      ref={ref}
      key={`media_${cm?.provisional_id}_${index}`}
      className="relative flex justify-center h-[150px] min-w-[50px] max-w-[300px] items-center group"
      data-provisional-id={cm?.provisional_id}
    >
      {/* Edit link - wrapped around the media content */}
      <Thumbnail
        allowEdit={allowEdit}
        cm={cm}
        blurred={bluredImage}
        hasNoPointer={hasNoPointer}
      />
      {/* Delete button */}
      <button
        type="button"
        className={`absolute -top-1 -right-1 bg-white border hover:bg-gray-100 rounded-sm cursor-pointer ${hasNoPointer ? "flex" : "hidden group-hover:flex"
          } duration-500 z-20`}
        onClick={() => deleteContentMedia(cm?.provisional_id)}
      >
        <XMarkIcon className="size-5" />
      </button>
      <button
        type="button"
        className={`absolute top-4 mt-1 -right-1 bg-white border hover:bg-gray-100 rounded-sm cursor-pointer ${hasNoPointer ? "flex" : "hidden group-hover:flex"
          } duration-500 z-20`}
      >
        <MdDragIndicator className="cursor-grabbing size-5 bg-white" />
      </button>
    </div>
  );
};

type Props = {
  allowEdit: boolean;
  disabled?: boolean;
  accept?: string;
  hContentMedia: Record<number, ProvisionalContentMedium>;
  selectedContentMedias: ProvisionalContentMedium["provisional_id"][] | [];
  beforeRemoveFn: (
    contentMediaId: ProvisionalContentMedium["provisional_id"]
  ) => void;
  afterMoveFn: (contentMediaId: number, newPosition: number) => void;
};

const ContentMediaList = ({
  allowEdit,
  hContentMedia,
  selectedContentMedias,
  beforeRemoveFn,
  afterMoveFn,
}: Props) => {
  const [hasNoPointer, setHasNoPointer] = useState(false);
  useEffect(() => {
    setHasNoPointer(window.matchMedia("(hover: none)").matches);
  });

  console.log(hasNoPointer);

  const deleteContentMedia = (contentMediaId: number) => {
    beforeRemoveFn(contentMediaId);
  };
  const changePosition = (
    contentMedia: ProvisionalContentMedium,
    newPosition: number
  ) => {
    afterMoveFn(contentMedia.provisional_id, newPosition);
  };
  const sortedCM: ProvisionalContentMedium[] = useMemo(
    () =>
      selectedContentMedias
        ?.map((cmId) => hContentMedia[cmId])
        .filter((c) => c),
    [selectedContentMedias, hContentMedia]
  );
  const isMobile = useIsMobile();

  return (
    <div className="flex w-full justify-start">
      {/* On affiche les content Medias affiliés au body */}
      <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
        <div className="w-full flex flex-wrap gap-2">
          {sortedCM?.map((cm, index) => (
            <DraggableMedia
              allowEdit={allowEdit}
              key={`cm_${cm?.provisional_id}_${index}`}
              cm={cm}
              index={index}
              deleteContentMedia={deleteContentMedia}
              changePosition={changePosition}
              hasNoPointer={hasNoPointer}
            />
          ))}
        </div>
      </DndProvider>
    </div>
  );
};

const Thumbnail = ({
  allowEdit,
  cm,
  blurred,
  hasNoPointer,
}: {
  allowEdit: boolean;
  cm: any;
  blurred: boolean;
  hasNoPointer?: boolean;
}) => {
  const [controlledBlur, setControlledBlur] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState<string | null>(null);

  useEffect(() => {
    setCurrentUrl(
      cm?.edit?.transformedUrl || cm.url || cm.serialized_file?.url
    );
  }, []);

  useEffect(() => {
    if (blurred) {
      setControlledBlur(true);
    }
  }, [blurred]);

  useEffect(() => {
    if (cm?.edit?.transformedUrl !== currentUrl) {
      setLoaded(false);
      setNewUrl(cm.edit?.transformedUrl);
    }
  }, [cm?.edit?.transformedUrl]);

  const handleLoaded = () => {
    setControlledBlur(false);
    setNewUrl(null);
    setCurrentUrl(newUrl);
    setLoaded(true);
  };

  const getEditLink = () => {
    if (
      !cm ||
      cm?.serialized_file?.resource_type !== "image" ||
      controlledBlur ||
      !allowEdit
    ) {
      return;
    }

    const baseParams = {
      content_medium_id: cm.id,
      "bg_image[url]": cm.serialized_file?.url,
      "bg_image[height]": cm.height || cm.serialized_file?.height,
      "bg_image[width]": cm.width || cm.serialized_file?.width,
      origin: "content_media_edit_modal",
      ajax_call: true,
    };

    // Build the query string with encoded parameters
    const queryString = Object.entries(baseParams)
      .filter(([_k, v]) => v)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    // If there's an existing edit, link to that edit; otherwise link to new
    const path = cm.edit
      ? `/content_media/edits/${cm.edit.id}/edit?${queryString}`
      : `/content_media/edits/new?${queryString}`;

    return path;
  };

  // On rend image ou video selon
  type ImageProps = React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >;

  type VideoProps = React.DetailedHTMLProps<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    HTMLVideoElement
  >;

  type MediaProps = {
    format: "image" | "video";
  } & (ImageProps | VideoProps);

  const cldImage = cld
    .image(cm.serialized_file?.public_id)
    .setAssetType("image")
    .format("jpg")
    .addTransformation("pg_1")
    .resize(thumbnail().width(320));

  const renderMedium = (props: MediaProps): React.ReactNode => {
    const { format, ...restProps } = props;

    if (cm.serialized_file?.format === "pdf") {
      console.log("PDF format detected");
      return (
        <AdvancedImage
          className={`object-cover h-full w-full absolute ${controlledBlur ? "blur-sm" : ""
            }`}
          key={`cm-${cm.id}`}
          cldImg={cldImage}
        />
      );
    }

    switch (format) {
      case "image":
        return <img {...(restProps as ImageProps)} />;
      case "video":
        return <video {...(restProps as VideoProps)} />;
      default:
        return null;
    }
  };

  return (
    <a
      href={getEditLink()}
      data-turbo-frame="content_media_edit"
      onClick={() => {
        window.openDialog("content_media_edit_modal");
      }}
      className="relative h-full w-full rounded-md border overflow-hidden flex items-center justify-center"
    >
      <div className="relative w-full h-full flex">
        {controlledBlur && (
          <div className="absolute inset-0 flex items-center bg-white bg-opacity-30 justify-center z-20">
            <div className="h-6 w-6 border-2 border-brand_main border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {/* On affiche url en arrière-plan (cas où pas encore cloudinary) */}
        {currentUrl &&
          renderMedium({
            format: cm.format,
            src: currentUrl || "",
            className: `object-cover object-center h-full transition-opacity duration-500 ease-in-out ${currentUrl && !newUrl ? "opacity-100" : "opacity-0"
              } ${controlledBlur ? "blur-sm" : ""}`,
          })}
        {/* Si on a reçu la version cloudinary, on l'affiche par dessus (onload pour éviter le flicker) */}
        {newUrl &&
          renderMedium({
            format: cm.format,
            src: newUrl,
            className: `object-cover h-full w-full transition-opacity duration-100 ease-in-out
             ${loaded ? "opacity-100" : "opacity-0 hidden"} ${controlledBlur ? "blur-sm" : ""
              }`,
            onLoad: handleLoaded,
          })}
        {cm.id &&
          cm?.serialized_file?.resource_type === "image" &&
          !controlledBlur &&
          allowEdit && (
            <div
              className={`absolute bg-black bg-opacity-50 opacity-0 flex ${hasNoPointer
                ? "opacity-100 px-2 py-1 rounded-full top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2"
                : "group-hover:opacity-100 inset-0"
                } items-center justify-center transition-opacity duration-300`}
            >
              <span className="text-white font-medium text-sm hover:text-gray-100 duration-200">
                Éditer
              </span>
            </div>
          )}
      </div>
    </a>
  );
};

export default ContentMediaList;
